import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Activity, AlertTriangle, CheckCircle2, Truck, HardHat,
  ClipboardList, Database, ShieldAlert, Zap, FolderTree, ArrowRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const SEV_STYLES = {
  critical: "border-red-500/60 bg-red-500/10",
  warning:  "border-yellow-500/60 bg-yellow-500/10",
  success:  "border-green-500/40 bg-green-500/5",
  info:     "border-[#2A2A2A] bg-[#1A1A1A]",
};

const KIND_ICON = {
  "DAILY REPORT":    ClipboardList,
  "PRE-OP":          Truck,
  "INCIDENT":        AlertTriangle,
  "SAFETY MEETING":  HardHat,
  "QA/QC":           CheckCircle2,
};

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const mins = Math.max(0, Math.round((now - d) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

export default function OperationsCommandView() {
  const [loading, setLoading] = useState(true);
  const [feed, setFeed] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [backups, setBackups] = useState([]);
  const [stats, setStats] = useState({ incidents: 0, oos: 0, meetings: 0 });

  useEffect(() => {
    let ok = true;
    const load = async () => {
      try {
        const [f, j, e] = await Promise.all([
          axios.get(`${API}/demo/activity-feed`),
          axios.get(`${API}/jobs`),
          axios.get(`${API}/equipment-master`),
        ]);
        if (!ok) return;
        const events = f?.data?.events || [];
        setFeed(events);
        setJobs(j?.data?.items || []);
        setEquipment(e?.data?.items || []);
        setStats({
          incidents: events.filter((x) => x.kind === "INCIDENT").length,
          oos: events.filter(
            (x) => x.kind === "PRE-OP" && x.severity === "critical"
          ).length,
          meetings: events.filter((x) => x.kind === "SAFETY MEETING").length,
        });
      } catch {/* swallow — demo is open */}
      finally { if (ok) setLoading(false); }
    };
    load();
    // Attempt to pull backups only if admin token present (soft-fail)
    try {
      const tok = window.localStorage.getItem("masci.admin.token");
      if (tok) {
        axios.get(`${API}/admin/backups`, {
          headers: { "X-Admin-Token": tok },
        }).then((r) => {
          if (ok) setBackups(r?.data?.items || r?.data || []);
        }).catch(() => {});
      }
    } catch {/* */}
    return () => { ok = false; };
  }, []);

  const oosEquipment = equipment.filter((u) => u.out_of_service);
  const greenEquipment = equipment.filter((u) => !u.out_of_service);

  return (
    <section
      data-testid="ops-command-view"
      className="mb-10 border-2 border-[#2A2A2A] rounded-xl overflow-hidden"
      style={{ background: "var(--fo-panel)" }}
    >
      {/* Header strip */}
      <div
        className="px-5 sm:px-7 py-4 border-b-2 border-[#2A2A2A] flex items-center justify-between gap-4 flex-wrap"
        style={{ background: "#0F0F0F" }}
      >
        <div className="flex items-center gap-3">
          <span
            className="inline-flex items-center justify-center w-10 h-10 rounded-md"
            style={{ background: "rgba(255,106,0,0.15)", color: "#FF6A00" }}
          >
            <Zap className="w-5 h-5" />
          </span>
          <div>
            <div
              className="font-mono text-[11px] uppercase tracking-[0.3em]"
              style={{ color: "#FF6A00" }}
            >
              FORGEDOPS Operations Command
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-white">
              Live jobsite status
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.15em] text-[#BDBDBD]">
          <span
            className="inline-flex w-2 h-2 rounded-full animate-pulse"
            style={{ background: "#2E7D32" }}
          />
          Live · auto-routing active
        </div>
      </div>

      {/* Capability tags — RUNS · TRACKS · DOCUMENTS · IDENTIFIES (matches forgedopshq.com) */}
      <div
        className="px-5 sm:px-7 py-2.5 border-b border-[#2A2A2A] flex flex-wrap items-center gap-3 text-[10px] font-mono uppercase tracking-[0.3em]"
        style={{ background: "#0B0B0B", color: "#BDBDBD" }}
      >
        <span style={{ color: "#FF6A00" }}>SYSTEM CAPABILITIES</span>
        <span style={{ color: "#3A3A3A" }}>·</span>
        <span>RUNS</span>
        <span>TRACKS</span>
        <span>DOCUMENTS</span>
        <span>IDENTIFIES</span>
        <span>ROUTES</span>
        <span>BACKS UP</span>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#2A2A2A]">
        <StatCell
          label="Active Jobs"
          value={jobs.length}
          icon={FolderTree}
          accent="#FF6A00"
          testId="ops-stat-jobs"
        />
        <StatCell
          label="Equipment · OOS"
          value={oosEquipment.length}
          sub={`/ ${equipment.length} total`}
          icon={Truck}
          accent={oosEquipment.length > 0 ? "#D32F2F" : "#2E7D32"}
          testId="ops-stat-oos"
        />
        <StatCell
          label="Incidents (recent)"
          value={stats.incidents}
          icon={AlertTriangle}
          accent="#FFD000"
          testId="ops-stat-incidents"
        />
        <StatCell
          label="Safety Meetings"
          value={stats.meetings}
          icon={HardHat}
          accent="#BDBDBD"
          testId="ops-stat-meetings"
        />
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-[#2A2A2A]">
        {/* Equipment status board */}
        <div
          className="lg:col-span-5 p-5 sm:p-6"
          style={{ background: "var(--fo-panel)" }}
          data-testid="ops-equipment-board"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg font-black tracking-tight text-white">
              Equipment status board
            </h3>
            <Link
              to="/admin/equipment"
              className="text-[10px] font-mono uppercase tracking-[0.2em] hover:underline"
              style={{ color: "#FF6A00" }}
            >
              Open dashboard →
            </Link>
          </div>
          {loading ? (
            <SkeletonRows n={5} />
          ) : (
            <div className="space-y-1.5">
              {oosEquipment.map((u) => (
                <EquipRow key={u.id || u.unit_number} u={u} oos />
              ))}
              {greenEquipment.map((u) => (
                <EquipRow key={u.id || u.unit_number} u={u} oos={false} />
              ))}
            </div>
          )}
        </div>

        {/* Live activity feed */}
        <div
          className="lg:col-span-7 p-5 sm:p-6"
          style={{ background: "var(--fo-panel)" }}
          data-testid="ops-activity-feed"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg font-black tracking-tight text-white inline-flex items-center gap-2">
              <Activity className="w-4 h-4" style={{ color: "#FF6A00" }} />
              Real-time activity feed
            </h3>
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#BDBDBD]">
              last 40 events
            </span>
          </div>
          {loading ? (
            <SkeletonRows n={6} />
          ) : (
            <div
              className="max-h-[420px] overflow-y-auto pr-2 space-y-2"
              data-testid="ops-activity-feed-list"
            >
              {feed.map((e, i) => {
                const Icon = KIND_ICON[e.kind] || Activity;
                const sev = SEV_STYLES[e.severity] || SEV_STYLES.info;
                return (
                  <div
                    key={`${e.id || i}-${e.kind}`}
                    className={`flex items-start gap-3 p-3 rounded-lg border ${sev}`}
                    data-testid="ops-feed-event"
                  >
                    <span
                      className="inline-flex items-center justify-center w-8 h-8 rounded-md shrink-0"
                      style={{
                        background: "rgba(255,106,0,0.12)",
                        color: "#FF6A00",
                      }}
                    >
                      <Icon className="w-4 h-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#BDBDBD]">
                          {e.kind}
                        </span>
                        {e.job_number && (
                          <span
                            className="font-mono text-[10px] px-1.5 py-0.5 rounded"
                            style={{
                              background: "rgba(255,106,0,0.12)",
                              color: "#FF6A00",
                              border: "1px solid rgba(255,106,0,0.3)",
                            }}
                          >
                            {e.job_number}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-white font-semibold mt-0.5 truncate">
                        {e.title}
                      </div>
                      {e.subtitle && (
                        <div className="text-xs text-[#BDBDBD] truncate">
                          {e.subtitle}
                        </div>
                      )}
                    </div>
                    <div className="text-[10px] font-mono text-[#BDBDBD] shrink-0">
                      {formatWhen(e.at)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Routing + Backup strip */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#2A2A2A]">
        <RoutingPanel />
        <BackupsPanel backups={backups} />
      </div>
    </section>
  );
}

function StatCell({ label, value, sub, icon: Icon, accent, testId }) {
  return (
    <div
      className="px-5 py-4 flex items-center gap-3"
      style={{ background: "var(--fo-panel)" }}
      data-testid={testId}
    >
      <span
        className="inline-flex items-center justify-center w-10 h-10 rounded-md shrink-0"
        style={{ background: `${accent}22`, color: accent }}
      >
        <Icon className="w-5 h-5" />
      </span>
      <div className="min-w-0">
        <div
          className="font-mono text-[10px] uppercase tracking-[0.2em]"
          style={{ color: "#BDBDBD" }}
        >
          {label}
        </div>
        <div className="flex items-baseline gap-2">
          <div
            className="font-display text-3xl font-black"
            style={{ color: accent }}
          >
            {value}
          </div>
          {sub && (
            <div className="font-mono text-[11px] text-[#BDBDBD]">{sub}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function EquipRow({ u, oos }) {
  return (
    <div
      className="flex items-center gap-3 p-2.5 rounded-md border"
      style={{
        background: oos ? "rgba(211,47,47,0.08)" : "var(--fo-bg-elev)",
        borderColor: oos ? "rgba(211,47,47,0.4)" : "var(--fo-border)",
      }}
      data-testid={oos ? "ops-eq-oos-row" : "ops-eq-green-row"}
    >
      <span
        className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
        style={{ background: oos ? "#D32F2F" : "#2E7D32" }}
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-white truncate">
          {u.unit_number}
          <span className="ml-2 font-normal text-[#BDBDBD] text-xs">
            {u.make_model}
          </span>
        </div>
        {oos && u.oos_reason && (
          <div className="text-[11px] text-red-400 truncate">
            {u.oos_reason}
          </div>
        )}
      </div>
      <span
        className={`font-mono text-[9px] px-2 py-0.5 rounded-full uppercase tracking-[0.2em] font-bold ${
          oos ? "fo-pill-critical" : "fo-pill-success"
        }`}
      >
        {oos ? "LOCKED OUT" : "READY"}
      </span>
    </div>
  );
}

function RoutingPanel() {
  const FOLDERS = [
    { label: "PM Folder", icon: FolderTree, count: "auto" },
    { label: "Safety Folder", icon: ShieldAlert, count: "auto" },
    { label: "Job Folder", icon: FolderTree, count: "auto" },
    { label: "Admin Records", icon: Database, count: "auto" },
    { label: "Backup System", icon: Database, count: "auto" },
  ];
  return (
    <div
      className="p-5 sm:p-6"
      style={{ background: "var(--fo-panel)" }}
      data-testid="ops-routing-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-lg font-black tracking-tight text-white">
          Auto-routing active
        </h3>
        <span className="fo-pill-routed">ON · every submit</span>
      </div>
      <p className="text-xs text-[#BDBDBD] mb-3 leading-relaxed">
        Every field submission lands in multiple places automatically — no
        manual email, no lost paperwork, no missing documentation.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {FOLDERS.map((f) => (
          <div
            key={f.label}
            className="flex items-center gap-2 px-2.5 py-2 rounded-md border"
            style={{
              borderColor: "var(--fo-border)",
              background: "var(--fo-bg-elev)",
            }}
          >
            <f.icon className="w-3.5 h-3.5" style={{ color: "#FF6A00" }} />
            <span className="text-[11px] font-mono uppercase tracking-[0.15em] text-white truncate">
              {f.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BackupsPanel({ backups }) {
  const list = backups && backups.length ? backups.slice(0, 5) : [];
  return (
    <div
      className="p-5 sm:p-6"
      style={{ background: "var(--fo-panel)" }}
      data-testid="ops-backups-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-lg font-black tracking-tight text-white">
          Backup &amp; Recovery
        </h3>
        <span className="fo-pill-success">HEALTHY</span>
      </div>
      <p className="text-xs text-[#BDBDBD] mb-3 leading-relaxed">
        Nightly encrypted snapshots · off-site mirror · 14-day retention ·
        one-click restore.
      </p>
      {list.length === 0 ? (
        <DemoBackupsList />
      ) : (
        <div className="space-y-1.5">
          {list.map((b) => (
            <BackupRow key={b.id || b.created_at} b={b} />
          ))}
        </div>
      )}
      <div className="mt-3 flex items-center gap-2">
        <button
          className="fo-btn-primary text-[11px]"
          data-testid="ops-backup-now-btn"
          onClick={() => {
            // eslint-disable-next-line no-alert
            alert(
              "Demo · Manual backup queued. Runs instantly in live deployments."
            );
          }}
        >
          Run Backup Now
        </button>
        <Link
          to="/admin"
          className="text-[11px] font-mono uppercase tracking-[0.15em] hover:underline"
          style={{ color: "#FF6A00" }}
        >
          Full history <ArrowRight className="inline w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}

function DemoBackupsList() {
  const [demoBackups, setDemoBackups] = useState([]);
  useEffect(() => {
    let ok = true;
    axios.get(`${API}/demo/backups`).then((r) => {
      if (ok) setDemoBackups(r?.data?.items || []);
    }).catch(() => {});
    return () => { ok = false; };
  }, []);
  return (
    <div className="space-y-1.5">
      {demoBackups.slice(0, 5).map((b) => (
        <BackupRow key={b.id} b={b} />
      ))}
      {demoBackups.length === 0 && (
        <div className="text-[11px] text-[#BDBDBD] italic">
          No snapshots yet — run one with the button below.
        </div>
      )}
    </div>
  );
}

function BackupRow({ b }) {
  const mb = b.size_bytes
    ? `${(b.size_bytes / (1024 * 1024)).toFixed(1)} MB`
    : "—";
  return (
    <div
      className="flex items-center gap-3 p-2 rounded-md border text-xs"
      style={{
        background: "var(--fo-bg-elev)",
        borderColor: "var(--fo-border)",
      }}
    >
      <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "#2E7D32" }} />
      <div className="flex-1 min-w-0">
        <div className="text-white font-mono truncate">{b.kind}</div>
        <div className="text-[10px] text-[#BDBDBD] truncate">
          {b.location || b.records || ""}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-white font-mono text-[11px]">{mb}</div>
        <div className="text-[10px] text-[#BDBDBD]">
          {formatWhen(b.taken_at || b.created_at)}
        </div>
      </div>
    </div>
  );
}

function SkeletonRows({ n = 4 }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: n }).map((_, i) => (
        <div
          key={i}
          className="h-10 rounded-md animate-pulse"
          style={{ background: "var(--fo-bg-elev)" }}
        />
      ))}
    </div>
  );
}
